import {
    createSelector
} from 'reselect';
import {
    printDateObjectAsAmericanDate,
    setSpecificDayOfWeekOnDate,
    setSpecificDayOfMonthOnDate,
    setSpecificMonthOnDate,
    getDayOfWeekFromDate,
    getMonthFromDate,
    printDateObjectAsFormattedString,
    addDaysToDate,
    addMonthsToDate,
    checkIfTwoDatesAreOnTheSameDay,
    checkIfTwoDatesAreInTheSameMonth,
    checkIfDateIsAfterOtherDate,
    checkIfDateIsBeforeOtherDate,
} from '../../helpers';

const getMinDate = (state, ownProps) => ownProps.minDate;
const getMaxDate = (state, ownProps) => ownProps.maxDate;
const getShowDays = (state, ownProps) => ownProps.showDays;
const getDisplayedDate = (state, ownProps) => ownProps.displayedDate;
const getValue = (state, ownProps) => ownProps.value;

export const selectFormattedMinimumDate = createSelector(
    [getMinDate],
    minDate => minDate ? printDateObjectAsAmericanDate(minDate) : ''
);

export const selectFormattedMaximumDate = createSelector(
    [getMaxDate],
    maxDate => maxDate ? printDateObjectAsAmericanDate(maxDate) : ''
);

export const selectGrid = createSelector(
    [getShowDays, getDisplayedDate, getValue, getMaxDate, getMinDate],
    (showDays, displayedDate, value, maxDate, minDate) => {
        // Prepare grid structure
        const rows = [];
        let daysCols = [];
        let monthsCols = [];

        // For days grid start from the Sunday from week before the first day of the month
        //    .e.g. February 01 is Monday; startDate will be January 31 (Sunday before)
        // For months grid start from January
        const startDate = showDays ?
            setSpecificDayOfWeekOnDate(setSpecificDayOfMonthOnDate(displayedDate, 1), 0, {
                weekStartsOn: 0,
            }) :
            setSpecificMonthOnDate(displayedDate, 0);

        // Clone start date
        let iteratedDate = startDate;

        // Iterate till grid of 6 rows is filled
        while (rows.length < 6) {
            // Create first day or month object
            if (showDays) {
                daysCols.push(createDateObject(iteratedDate, displayedDate, value, maxDate, minDate));
            } else {
                monthsCols.push(createMonthObject(iteratedDate, value));
            }

            // Iterate all row columns
            if (showDays) {
                // For days
                if (getDayOfWeekFromDate(iteratedDate) >= 6) {
                    rows.push({
                        key: printDateObjectAsFormattedString(iteratedDate, 'YYYY-Wo'),
                        days: daysCols,
                        months: [],
                    });
                    daysCols = [];
                }

                // Move to next day
                iteratedDate = addDaysToDate(iteratedDate, 1);
            } else {
                // For months
                if (monthsCols.length >= 2) {
                    rows.push({
                        key: printDateObjectAsFormattedString(iteratedDate, 'YYYY') + '-' + rows.length,
                        days: [],
                        months: monthsCols,
                    });
                    monthsCols = [];
                }

                // Move to next month
                iteratedDate = addMonthsToDate(iteratedDate, 1);
            }
        }
        iteratedDate = null;

        return {
            className: showDays ? 'calendar-days' : 'calendar-months',
            rows,
        };
    }
)

/**
 * Create single day date object
 * @param {Date} date
 * @returns GridDay
 */
const createDateObject = (date, displayedDate, value, maxDate, minDate) => {
    displayedDate = displayedDate ? displayedDate : '';
    value = value ? value : null;
    maxDate = maxDate ? maxDate : null;
    minDate = minDate ? minDate : null;

    const now = new Date();

    const isOlderThanMaxDate =
        maxDate && checkIfDateIsAfterOtherDate(date, maxDate) ? true : false;
    const isYoungerThanMinDate =
        minDate && checkIfDateIsBeforeOtherDate(date, minDate) ? true : false;

    return {
        id: printDateObjectAsFormattedString(date, 'YYYY-MM-DD'),
        date,
        dateString: printDateObjectAsFormattedString(date, 'DD'),
        isSelected: value !== null ? checkIfTwoDatesAreOnTheSameDay(date, value) : false,
        isCurrentMonth: checkIfTwoDatesAreInTheSameMonth(date, displayedDate),
        isToday: checkIfTwoDatesAreOnTheSameDay(date, now),
        isDisabled: isOlderThanMaxDate || isYoungerThanMinDate,
        isDisabledBecause: isOlderThanMaxDate ?
            'older-than-max-date' : isYoungerThanMinDate ?
            'younger-than-min-date' : null,
    };
};

/**
 * Create single month date object
 * @param {Date} date
 * @returns GridMonth
 */
const createMonthObject = (date, value) => {
    value = value ? value : null;

    return {
        id: printDateObjectAsFormattedString(date, 'YYYY-MM'),
        month: getMonthFromDate(date),
        monthString: printDateObjectAsFormattedString(date, 'MMMM'),
        isSelected: value !== null ? checkIfTwoDatesAreInTheSameMonth(date, value) : false,
    };
};



// WEBPACK FOOTER //
// ./app/web/src/components/fields/CalendarSelectors.js